import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { BILLING_PLAN_CODES } from "./billing.types";
import type { BillingOverview, BillingPlan, PublicBillingDocument } from "./billing.types";

export const getPublicBillingPlans = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ plans: BillingPlan[] }> => {
    try {
      const { fetchBillingPlans } = await import("./supabase.server");
      return { plans: await fetchBillingPlans() };
    } catch (error) {
      console.error("Preismodelle konnten nicht geladen werden", error);
      return { plans: [] };
    }
  },
);

export const getOwnBillingOverview = createServerFn({ method: "GET" }).handler(
  async (): Promise<BillingOverview> => {
    const { requireAuthenticatedAccount } = await import("./auth.server");
    const account = await requireAuthenticatedAccount();
    const { fetchBillingOverview } = await import("./supabase.server");
    return fetchBillingOverview(account.userId);
  },
);

export const getPublicBillingDocument = createServerFn({ method: "GET" })
  .validator((data: unknown) => z.object({ token: z.string().uuid() }).parse(data))
  .handler(async ({ data }): Promise<PublicBillingDocument> => {
    const { fetchPublicBillingDocument } = await import("./supabase.server");
    const document = await fetchPublicBillingDocument(data.token);
    if (!document) throw new Response("Rechnung nicht gefunden.", { status: 404 });
    return document;
  });

export const setOwnBillingCancellation = createServerFn({ method: "POST" })
  .validator((data: unknown) => z.object({ cancelAtPeriodEnd: z.boolean() }).parse(data))
  .handler(async ({ data }) => {
    const { requireAuthenticatedAccount } = await import("./auth.server");
    const account = await requireAuthenticatedAccount();
    const { setBillingSubscriptionCancellation } = await import("./supabase.server");
    return {
      subscription: await setBillingSubscriptionCancellation(
        account.userId,
        data.cancelAtPeriodEnd,
        `customer:${account.userId}`,
      ),
    };
  });

const billingAddressSchema = z.object({
  street: z.string().trim().min(2).max(120),
  buildingNumber: z.string().trim().max(20).default(""),
  postalCode: z.string().trim().min(3).max(16),
  city: z.string().trim().min(2).max(100),
  countryCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/)
    .default("CH"),
});

export const requestOwnBillingInvoice = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        planCode: z.enum(BILLING_PLAN_CODES),
        discountCode: z.string().trim().max(40).optional(),
        expectedAmount: z.number().int().nonnegative().optional(),
        customerName: z.string().trim().min(2).max(160),
        customerCompany: z.string().trim().max(160).optional().default(""),
        billingAddress: billingAddressSchema,
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { requireAuthenticatedAccount } = await import("./auth.server");
    const account = await requireAuthenticatedAccount();
    if (account.profile.role === "kunde" || account.profile.role === "admin") {
      throw new Response("Dieses Angebot ist nur für Anbieter verfügbar.", { status: 403 });
    }

    const { createBillingInvoiceRequest, fetchBillingOverview, fetchBillingSettings } =
      await import("./supabase.server");
    const settings = await fetchBillingSettings();
    if (!settings?.billingEnabled) {
      throw new Response("Die Rechnungsbestellung ist noch nicht freigeschaltet.", { status: 409 });
    }

    const invoice = await createBillingInvoiceRequest({
      userId: account.userId,
      planCode: data.planCode,
      discountCode: data.discountCode,
      expectedAmount: data.expectedAmount,
      customerName: data.customerName,
      customerCompany: data.customerCompany,
      customerEmail: account.email,
      billingAddress: data.billingAddress,
    });
    return { invoice, overview: await fetchBillingOverview(account.userId) };
  });
